import React, {useEffect, useState} from "react";
import {Autocomplete, Box, Fade, IconButton, InputBase, MenuItem, Modal, Select, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon} from "@hugeicons/core-free-icons";
import {
  controlSx, FieldHelper, FieldLabel, FlatAutocompleteInput, modalCancelSx, ModalButton, modalPaperSx, modalSaveSx,
  SecretField, selectSx,
} from "../../../components/modals/formFieldShared";

// suggestions only, the backend accepts any provider name
const PROVIDERS = ["OpenAI", "ElevenLabs", "Azure", "Google", "Amazon Polly", "Cartesia", "Custom"];

const EMPTY_MODEL = {
  name: "",
  provider: "OpenAI",
  modelId: "",
  baseUrl: "",
  apiKey: "",
  connectionString: "",
  organizationId: null,
};

const toFormModel = (model) => ({
  name: model.name || "",
  provider: model.provider || "OpenAI",
  modelId: model.modelId || "",
  baseUrl: model.baseUrl || "",
  apiKey: "",
  connectionString: "",
  organizationId: model.organizationId ?? null,
});

export const VoiceModelDialog = ({open, handleClose, selectedModel, organizations, onSubmit}) => {
  const [model, setModel] = useState(EMPTY_MODEL);
  const [loading, setLoading] = useState(false);

  const isEdit = Boolean(selectedModel?.id);

  useEffect(() => {
    setModel(selectedModel ? toFormModel(selectedModel) : EMPTY_MODEL);
  }, [selectedModel, open]);

  const onChange = (e) => {
    const {name, value} = e.target;
    setModel(prev => ({...prev, [name]: value}));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!model.name.trim()) return;
    setLoading(true);
    const success = await onSubmit({...model, provider: model.provider.trim() || "OpenAI"});
    setLoading(false);
    if (success) handleClose();
  };

  return (
    <Modal open={open} onClose={loading ? undefined : handleClose}>
      <Fade in={open}>
        <Box component={"form"} onSubmit={handleSubmit} sx={modalPaperSx(480)}>
          <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} sx={{p: 3, pb: 2}}>
            <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
              {isEdit ? "Edit" : "Add"} Voice Model
            </Typography>
            <IconButton size={"small"} onClick={loading ? undefined : handleClose} sx={{color: "#8A8AA0"}}>
              <HugeiconsIcon icon={Cancel01Icon} size={18}/>
            </IconButton>
          </Stack>

          <Stack gap={2} sx={{px: 3, pb: 1, overflow: "auto"}}>
            <Box>
              <FieldLabel required>Display Name</FieldLabel>
              <Box sx={controlSx}>
                <InputBase
                  name={"name"} autoFocus placeholder={"e.g. OpenAI TTS"} value={model.name}
                  onChange={onChange} inputProps={{"aria-label": "Display name"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                />
              </Box>
            </Box>

            <Box>
              <FieldLabel>Provider</FieldLabel>
              <Autocomplete
                freeSolo
                options={PROVIDERS}
                value={model.provider}
                onInputChange={(e, value) => setModel(prev => ({...prev, provider: value}))}
                renderInput={(props) => <FlatAutocompleteInput {...props} placeholder={"e.g. OpenAI"}/>}
              />
            </Box>

            <Box>
              <FieldLabel>Model ID</FieldLabel>
              <Box sx={controlSx}>
                <InputBase
                  name={"modelId"} placeholder={"e.g. tts-1"} value={model.modelId}
                  onChange={onChange} inputProps={{"aria-label": "Model ID"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                />
              </Box>
              <FieldHelper>Exact identifier used in API calls</FieldHelper>
            </Box>

            <Box>
              <FieldLabel>Base URL</FieldLabel>
              <Box sx={controlSx}>
                <InputBase
                  name={"baseUrl"} placeholder={"https://api.openai.com/v1"} value={model.baseUrl}
                  onChange={onChange} inputProps={{"aria-label": "Base URL"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                />
              </Box>
            </Box>

            {!isEdit && (
              <>
                <Box>
                  <FieldLabel>API Key</FieldLabel>
                  <SecretField name={"apiKey"} value={model.apiKey} onChange={onChange} placeholder={"sk-..."}/>
                  <FieldHelper>Stored encrypted and cannot be changed after creation</FieldHelper>
                </Box>
                <Box>
                  <FieldLabel>Connection String</FieldLabel>
                  <SecretField name={"connectionString"} value={model.connectionString} onChange={onChange} placeholder={"Optional, e.g. for Azure Speech"}/>
                  <FieldHelper>Stored encrypted and cannot be changed after creation</FieldHelper>
                </Box>
              </>
            )}

            <Box>
              <FieldLabel>Organization Access</FieldLabel>
              <Select
                value={model.organizationId ?? ""}
                onChange={(e) => setModel(prev => ({...prev, organizationId: e.target.value || null}))}
                sx={selectSx}
                MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
              >
                <MenuItem value={""}>Public (all users)</MenuItem>
                {organizations?.map(org => (
                  <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                ))}
              </Select>
              <FieldHelper>Leave empty to make this model available to all users</FieldHelper>
            </Box>
          </Stack>

          <Stack direction={"row"} gap={1.5} justifyContent={"flex-end"} sx={{p: 3, pt: 2}}>
            <ModalButton onClick={loading ? undefined : handleClose} sx={modalCancelSx}>Cancel</ModalButton>
            <ModalButton type={"submit"} disabled={loading} sx={modalSaveSx}>
              {isEdit ? "Update" : "Add Model"}
            </ModalButton>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};
